// Pure view-derivation for the QE/TE map view (T13): the truthful banner string parts and the
// sanitized hover label/props. Kept in a plain module (no JSX) so the status components can live in
// their own components-only file. No maplibre import.

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SpatialMapFeature, SpatialProbeContext } from '../map/spatialMapTypes';

/** The truthful banner string parts: "Showing first N of M features", byte-budget, "N too large to map". */
export function buildBannerParts(args: {
  truncated: boolean;
  rowsReturned: number;
  total: number;
  byteBudgetExceeded: boolean;
  tooLargeCount: number;
}): string[] {
  const parts: string[] = [];
  if (args.truncated) {
    parts.push(
      `Showing first ${args.rowsReturned.toLocaleString()} of ${args.total.toLocaleString()} features`,
    );
  }
  if (args.byteBudgetExceeded) parts.push('byte budget reached');
  if (args.tooLargeCount > 0) parts.push(`${args.tooLargeCount.toLocaleString()} too large to map`);
  return parts;
}

/**
 * Geometry-picker resolution: the picked column (panel state, possibly seeded from
 * `initialGeometryColumn`) wins only while it is a current spatial-column candidate;
 * otherwise auto-pick the first spatial column (or null when there is none).
 */
export function resolveGeometryPick(picked: string | null, spatialColumnIds: string[]): string | null {
  return picked && spatialColumnIds.includes(picked) ? picked : (spatialColumnIds[0] ?? null);
}

/**
 * Color-by-picker resolution: the picked column (possibly seeded from `initialColorBy`)
 * wins only while it is a current non-spatial column; otherwise None.
 */
export function resolveColorByPick(picked: string | null, nonSpatialColumnIds: string[]): string | null {
  return picked && nonSpatialColumnIds.includes(picked) ? picked : null;
}

export type HoverContent = { label: string | null; props: { key: string; value: unknown }[] };

/** Sanitized hover content: the first non-spatial column is the label, the rest become key/value rows. */
export function deriveHoverContent(
  feature: SpatialMapFeature | undefined,
  nonSpatialColumnIds: string[],
  columnLabels: Map<string, string>,
): HoverContent {
  if (!feature) return { label: null, props: [] };
  const labelColumnId = nonSpatialColumnIds[0] ?? null;
  const rawLabel = labelColumnId ? (feature.properties?.[labelColumnId] ?? null) : null;
  const props = nonSpatialColumnIds
    .filter((id) => id !== labelColumnId && feature.properties?.[id] !== undefined)
    .map((id) => ({ key: columnLabels.get(id) ?? id, value: feature.properties?.[id] }));
  return { label: rawLabel === null ? null : String(rawLabel), props };
}

/**
 * T28 — the `SpatialProbeContext` the coordinate probe needs, derived from data the panel already
 * computes (no new decode work). `null` when there's no resolved geometry column at all (the probe
 * still shows coordinates on Alt-hover with no `probeContext`; it just can't offer a copy). `kind`
 * is a coarse geometry/geography split from the dataType string — it only gates whether a copy is
 * OFFERED, never which SQL form is emitted (that's engine-only, see `spatialSqlLiterals.ts`).
 */
export function deriveProbeContext(args: {
  engine: DatabaseEngineId;
  hasGeometryColumn: boolean;
  isDeclared: boolean;
  dataType: string | undefined;
  distinctSrids: number[];
  surface: SpatialProbeContext['surface'];
}): SpatialProbeContext | null {
  if (!args.hasGeometryColumn) return null;
  const kind: SpatialProbeContext['kind'] = args.isDeclared
    ? 'declared-pair'
    : args.dataType?.toLowerCase().includes('geography')
      ? 'geography'
      : 'geometry';
  const srid = args.distinctSrids.length === 1 ? args.distinctSrids[0] : null;
  return { engine: args.engine, kind, srid, surface: args.surface };
}
